import { useState } from "react";
import { saveAs } from "file-saver";
import {
  DownloadIcon,
  EllipsisVerticalIcon,
  FileDownIcon,
  PencilIcon,
  PlusIcon,
  ReplaceAllIcon,
  StarIcon,
  Trash2Icon,
} from "lucide-react";

import { ConfirmationDialog, LoadingIndicator, Row } from "@/components";
import { RenameResumeDialog } from "./RenameResumeDialog";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuPortal,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

import { useErrorToast } from "@/hooks/useErrorToast";
import { useSuccessToast } from "@/hooks/useSuccessToast";
import { useUpdateProfileUser } from "@/hooks/mutations/useUpdateProfileUser";
import { novaClient } from "@/lib/trpc";
import { ResumeFileType, ResumeParserAction } from "@/types";

export const ResumeActionsMenu = ({
  onResumeUpdated,
  primaryResumeId,
  resumeFile,
  userId,
}: {
  onResumeUpdated: () => void;
  primaryResumeId: number | null;
  resumeFile: ResumeFileType;
  userId: number;
}) => {
  /* Database CRUD Operations */
  // Update
  const { mutate: updateUser } = useUpdateProfileUser();
  const { mutate: deleteResumeFile } =
    novaClient.internal.user.resume.active.delete.useMutation();
  const { mutate: parseResumeFile, isPending: isParsePending } =
    novaClient.internal.user.resume.active.parse.useMutation();

  /* React Hooks */
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [isOverwriteOpen, setIsOverwriteOpen] = useState(false);
  const [isRenameOpen, setIsRenameOpen] = useState(false);
  const [isSetPrimaryOpen, setIsSetPrimaryOpen] = useState(false);

  const novaUtils = novaClient.useUtils();
  const successToast = useSuccessToast();
  const errorToast = useErrorToast();

  /* Helpers */
  const isPrimaryResume = resumeFile.id === primaryResumeId;

  /* Handlers */
  const invalidateResumeData = () => {
    void novaUtils.internal.user.resume.active.listByUserId.invalidate({
      userId,
    });
    onResumeUpdated();
  };

  const handleDownload = () => {
    if (!resumeFile.url) return;
    saveAs(resumeFile.url, resumeFile.title || "Resume");
  };

  const handleDelete = () => {
    deleteResumeFile(
      { id: resumeFile.id },
      {
        onSuccess: () => {
          invalidateResumeData();
          setIsDeleteOpen(false);
        },
        onError: () => {
          errorToast({ description: "Failed to delete resume." });
        },
      },
    );
  };

  const handleParse = (action: ResumeParserAction) => {
    parseResumeFile(
      { action, id: resumeFile.id },
      {
        onSuccess: () => {
          successToast({ description: "Resume parsing queued successfully." });
          setIsOverwriteOpen(false);
        },
        onError: () => {
          errorToast({ description: "Failed to parse resume." });
        },
      },
    );
  };

  const handleSetPrimary = () => {
    updateUser(
      {
        data: { primaryResumeId: resumeFile.id },
        userId,
      },
      {
        onSuccess: () => {
          invalidateResumeData();
          setIsSetPrimaryOpen(false);
        },
        onError: () => {
          errorToast({ description: "Failed to set primary resume." });
        },
      },
    );
  };

  /* Render */
  const renderParseSubmenu = () => {
    return (
      <DropdownMenuSub>
        <DropdownMenuSubTrigger disabled={isParsePending}>
          <FileDownIcon className="h-4 w-4" />
          Parse resume to profile
        </DropdownMenuSubTrigger>
        <DropdownMenuPortal>
          <DropdownMenuSubContent className="text-slate-700">
            <DropdownMenuItem
              onClick={() => {
                handleParse(ResumeParserAction.PARSE_AND_RECONCILE);
              }}
            >
              <PlusIcon className="h-4 w-4" />
              Add to profile
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() => {
                setIsOverwriteOpen(true);
              }}
            >
              <ReplaceAllIcon className="h-4 w-4" />
              Overwrite profile
            </DropdownMenuItem>
          </DropdownMenuSubContent>
        </DropdownMenuPortal>
      </DropdownMenuSub>
    );
  };

  const render = () => {
    return (
      <>
        <Row className="items-center">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                className="h-8 w-8 shrink-0 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
                size="icon"
                title="Resume actions"
                type="button"
                variant="ghost"
              >
                <EllipsisVerticalIcon className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="text-slate-700">
              <DropdownMenuItem onClick={handleDownload}>
                <DownloadIcon className="h-4 w-4" />
                Download
              </DropdownMenuItem>
              {renderParseSubmenu()}
              <DropdownMenuItem
                onClick={() => {
                  setIsRenameOpen(true);
                }}
              >
                <PencilIcon className="h-4 w-4" />
                Rename document
              </DropdownMenuItem>
              <DropdownMenuItem
                disabled={isPrimaryResume}
                onClick={() => {
                  setIsSetPrimaryOpen(true);
                }}
              >
                <StarIcon className="h-4 w-4 fill-slate-300 stroke-slate-300" />
                Set primary resume
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => {
                  setIsDeleteOpen(true);
                }}
              >
                <Trash2Icon className="h-4 w-4" />
                Delete document
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          {isParsePending ? <LoadingIndicator variant="small" /> : null}
        </Row>
        <ConfirmationDialog
          confirmationButtonVariant="destructive"
          confirmButtonText="Overwrite"
          description="This will replace the candidate's entire profile with data parsed from the resume. This action cannot be undone."
          onConfirm={() => {
            handleParse(ResumeParserAction.OVERWRITE);
          }}
          onOpenChange={setIsOverwriteOpen}
          open={isOverwriteOpen}
          title="Overwrite profile?"
        />
        <ConfirmationDialog
          description={`This will set "${resumeFile.title}" as the candidate's primary resume, making it visible to recruiters.`}
          onConfirm={handleSetPrimary}
          onOpenChange={setIsSetPrimaryOpen}
          open={isSetPrimaryOpen}
          title="Are you sure?"
        />
        <ConfirmationDialog
          confirmationButtonVariant="destructive"
          confirmButtonText="Delete"
          description={`This will fully delete "${resumeFile.title}" from the candidate's profile.`}
          onConfirm={handleDelete}
          onOpenChange={setIsDeleteOpen}
          open={isDeleteOpen}
          title="Are you sure?"
        />
        <RenameResumeDialog
          onOpenChange={setIsRenameOpen}
          onRenameSuccess={invalidateResumeData}
          open={isRenameOpen}
          selectedResumeFile={resumeFile}
          transformedCandidateDocument={{ userId }}
        />
      </>
    );
  };

  return render();
};
